import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import { formatDocumentCategory, formatSnakeValue } from 'src/lib/formatters'
import { isSameUtcDay } from 'src/utils/date/toMonthYearKey'

dayjs.extend(utc)

export const AI_HIGH_CONFIDENCE_THRESHOLD = 0.9

// NOTE: the display-confidence threshold lives entirely on the backend, which
// drops sub-threshold predictions from the GraphQL response (see
// api/src/lib/documents/documentIntelligence.ts). Every prediction the frontend
// receives is therefore already confident enough to show — the UI infers an AI
// abstention from the ABSENCE of a prediction for a field AI runs on.

// Human labels for the "AI Extracted" popover. Falls back to a title-cased version
// of the enum for any field not listed here, so newly added prediction fields
// render sensibly without a code change.
export const EXTRACTED_FIELD_LABELS: Record<string, string> = {
  CATEGORY: 'Category',
  DATE: 'Expiration',
  LICENSE_NUMBER: 'License #',
  LICENSE_STATE: 'State',
  LICENSE_CLASS: 'Class',
  LICENSE_ENDORSEMENTS: 'Endorsements',
  DOCUMENT_SIDE: 'Side',
  FILE_NAME: 'File name',
}

// Fields that participate in the accept/reject review workflow by default (they
// map to editable Document columns). License details are extraction-only UNLESS
// they were explicitly dismissed from the License Information card — see
// resolveReviewStatus.
const REVIEWABLE_FIELDS = ['CATEGORY', 'DATE']

// Display order in the popover — primary fields first, then license details.
const FIELD_ORDER = [
  'CATEGORY',
  'DATE',
  'LICENSE_NUMBER',
  'LICENSE_STATE',
  'LICENSE_CLASS',
  'LICENSE_ENDORSEMENTS',
  'DOCUMENT_SIDE',
  'FILE_NAME',
]

// Formats a backend AiExtractedRow value for display. The backend emits RAW
// values (the enum for CATEGORY, an ISO timestamp for DATE / med-cert expiry) so
// presentation has a single source of truth here, on the frontend. Everything
// else is already display-ready text. Used for rendering AND for the
// value-of-record mismatch comparison, so both sides are formatted the same way.
export function formatExtractedRowValue(field: string, value: string): string {
  if (field === 'CATEGORY') return formatDocumentCategory(value) ?? value
  if (field === 'DATE' || field === 'MVR_MEDICAL_CERT_EXPIRATION') {
    return value ? dayjs.utc(value).format('MM/DD/YYYY') : value
  }
  return value
}

// Formats a finding record's raw ISO event date for the hover detail.
export const formatExtractedRecordDate = (
  date?: string | null
): string | null => (date ? dayjs.utc(date).format('MM/DD/YYYY') : null)

// Formats a finding-detail value (e.g. ACD code, Points, Conviction date) for
// display. Detail values arrive raw from the backend; date-typed fields come as
// ISO timestamps, so format anything that looks like one. Shared by the
// doc-preview hover (ExtractedFieldRow) and the Application-tab MVR pills so
// both render details identically.
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T/
export const formatExtractedDetailValue = (value: string): string =>
  ISO_TIMESTAMP.test(value) ? formatExtractedRecordDate(value) ?? value : value

export interface ExtractedFieldPrediction {
  field: string
  textValue?: string | null
  categoryValue?: string | null
  expirationValue?: string | null
  confidence?: number | null
  autoApplied?: boolean | null
  reviewDecision?: string | null
  // EAV finding-group fields: rows sharing a findingGroupId form one logical
  // finding (e.g. one MVR accident). See DocumentPrediction in schema.prisma.
  findingGroupId?: string | null
  findingType?: string | null
}

// accepted = AI value applied to the document (auto or via Apply)
// rejected  = recruiter kept their own value (KEPT_ORIGINAL)
// pending   = reviewable but not yet acted on
// null      = not a reviewable field (license details — extraction only)
export type ReviewStatus = 'accepted' | 'rejected' | 'pending'

export interface ExtractedField {
  field: string
  label: string
  value: string
  confidence: number
  reviewStatus: ReviewStatus | null
}

// The displayed value lives in a different column depending on the field type:
// CATEGORY → categoryValue (enum), DATE → expirationValue (date),
// everything else → textValue. Returns null when nothing was extracted.
function resolvePredictionValue(p: ExtractedFieldPrediction): string | null {
  if (p.field === 'CATEGORY') {
    return p.categoryValue ? formatDocumentCategory(p.categoryValue) : null
  }
  if (p.field === 'DATE') {
    return p.expirationValue
      ? dayjs.utc(p.expirationValue).format('MM/DD/YYYY')
      : null
  }
  const text = p.textValue?.trim()
  return text ? text : null
}

// Review status for the popover pill color.
// An explicit decision applies to ANY field — including a license detail
// dismissed (KEPT_ORIGINAL) from the License Information card, which should read
// as rejected here too. With no decision, only the reviewable core fields are
// "pending"; license details are extraction-only and get no status (blue).
function resolveReviewStatus(p: ExtractedFieldPrediction): ReviewStatus | null {
  if (p.reviewDecision === 'KEPT_ORIGINAL') return 'rejected'
  if (p.reviewDecision === 'ACCEPTED_AI' || p.autoApplied) return 'accepted'
  return REVIEWABLE_FIELDS.includes(p.field) ? 'pending' : null
}

// Builds the list for the read-only "AI Extracted" popover: every field the model
// pulled off the document, including the core fields (Category, Expiration) that
// also appear in the editable strip — so the popover is a complete record of what
// the AI read. Intentionally ignores reviewDecision/autoApplied: we always show
// what was extracted, even after a recruiter overrides a suggestion. Predictions
// with no extracted value (e.g. a blank DOCUMENT_SIDE) are skipped.
interface ActualValueSources {
  document: { category?: string | null; expirationDate?: string | null }
  license?: {
    licenseNumber?: string | null
    state?: string | null
    licenseClass?: string | null
    expirationDate?: string | null
    endorsements?: string[] | null
    medicalExamExpirationDate?: string | null
  } | null
}

// Builds the "value of record" for each field, shown in the popover for
// comparison against the AI extraction. Category comes from the document.
// License details come from the application's current license.
// Expiration should be compared against application license expiration (or
// medical-exam expiration for DOT medical cards), because that's the canonical
// value shown to recruiters in the Application tab. Fallback to document date
// only when no application license expiration exists.
export function buildActualValues({
  document,
  license,
}: ActualValueSources): Record<string, string | null> {
  const expiration =
    document.category === 'DOT_MEDICAL'
      ? license?.medicalExamExpirationDate
      : license?.expirationDate ?? document.expirationDate
  // When license data hasn't loaded yet, return null so valuesMismatch skips
  // comparison (nothing to compare against). When license IS loaded, return ''
  // for empty fields — that signals "stored value is intentionally blank" and
  // lets valuesMismatch flag the AI extraction as a discrepancy, matching the
  // behavior of the License Information card.
  const licenseLoaded = license != null
  return {
    CATEGORY: document.category
      ? formatDocumentCategory(document.category)
      : null,
    DATE: expiration
      ? dayjs.utc(expiration).format('MM/DD/YYYY')
      : null,
    LICENSE_NUMBER: licenseLoaded ? (license.licenseNumber ?? '') : null,
    LICENSE_STATE: licenseLoaded ? (license.state ?? '') : null,
    LICENSE_CLASS: licenseLoaded ? (license.licenseClass ?? '') : null,
    LICENSE_ENDORSEMENTS: licenseLoaded
      ? (license.endorsements?.length ? license.endorsements.join(', ') : '')
      : null,
  }
}

// Canonical license-number normalization, shared by the License Information
// card mismatch check (see licenseNumberComparison.ts, which re-exports this)
// and the popover/edit-screen comparison below. Strips formatting + casing,
// and additionally drops leading zeros from purely-numeric values — OCR often
// prepends them ("0003786130" vs "3786130") — keeping a lone "0" rather than
// emptying all-zero input.
export const normalizeLicenseNumber = (
  value: string | null | undefined
): string => {
  if (!value) return ''
  const sanitized = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
  if (!/^\d+$/.test(sanitized)) return sanitized
  return sanitized.replace(/^0+(?=\d)/, '')
}

// Field-aware normalization for the popover mismatch cue. Differences that are
// purely formatting (spacing, punctuation, endorsement order) must NOT flag a
// mismatch — only genuinely different values should.
function normalizeForComparison(field: string, value: string): string {
  const upper = value.trim().toUpperCase()
  switch (field) {
    case 'LICENSE_ENDORSEMENTS': {
      // Order-insensitive set with X ≡ H+N equivalence.
      // X (Hazmat+Tank combo) grants the same privileges as holding H and N
      // separately, so "X, T" and "H, N, T" must not flag as a mismatch.
      const codes = new Set(upper.replace(/[^A-Z]/g, '').split('').filter(Boolean))
      if (codes.has('X') || (codes.has('H') && codes.has('N'))) {
        codes.add('H')
        codes.add('N')
        codes.add('X')
      }
      return [...codes].sort().join('')
    }
    case 'LICENSE_NUMBER':
      // Strip formatting/casing + leading zeros — see normalizeLicenseNumber.
      return normalizeLicenseNumber(value)
    default:
      // States, classes, dates, category: collapse whitespace only.
      return upper.replace(/\s+/g, '')
  }
}

// Returns true when the AI value and the value of record genuinely differ.
// Returns false when AI extracted nothing (blank → nothing to flag) or when the license hasn't
// loaded yet (actualValue == null). An empty-string actualValue IS meaningful: it means the
// license is loaded but the field is blank, which flags a mismatch when AI extracted a value.
export function valuesMismatch(
  field: string,
  aiValue?: string | null,
  actualValue?: string | null
): boolean {
  if (!aiValue || actualValue == null) return false
  return (
    normalizeForComparison(field, aiValue) !==
    normalizeForComparison(field, actualValue)
  )
}

// A document carrying AI predictions, as fetched for the checklist sidebar.
// Loosely typed because the generated GraphQL `Document` type is a moving target;
// only the fields this helper reads are declared.
export interface AiReviewableDocument {
  category?: string | null
  expirationDate?: string | null
  documentIntelligence?: {
    status?: string | null
    predictions?: ExtractedFieldPrediction[] | null
  } | null
}

// Predictions on a document that still need recruiter review — the same set
// DocumentPreview surfaces as Apply/Dismiss action cards (see
// `lowConfidencePredictions` there), minus the per-session dismissed state which
// only exists while the preview is open. Keeping this logic in one place ensures
// the checklist badge count and the preview's action cards never disagree.
//
// A prediction is "pending" when: extraction completed, it's a reviewable core
// field (Category/Expiration), confident enough to prompt an action, not already
// auto-applied or explicitly decided, has a value, and that value differs from
// what's currently stored on the document (a matching value means the recruiter
// already accepted it in a prior session). Deduped by field — the classify and
// extract passes can both emit DATE — keeping the highest confidence.
export function getPendingAiPredictions(
  doc: AiReviewableDocument | null | undefined
): ExtractedFieldPrediction[] {
  const di = doc?.documentIntelligence
  if (!di || di.status !== 'COMPLETED' || !di.predictions) return []

  const byField = new Map<string, ExtractedFieldPrediction>()
  for (const p of di.predictions) {
    if (p.autoApplied) continue
    if (p.field !== 'DATE' && p.field !== 'CATEGORY') continue
    if (
      p.reviewDecision === 'KEPT_ORIGINAL' ||
      p.reviewDecision === 'ACCEPTED_AI'
    )
      continue
    if (!p.expirationValue && !p.categoryValue) continue
    if (
      p.field === 'DATE' &&
      p.expirationValue &&
      doc?.expirationDate &&
      isSameUtcDay(p.expirationValue, doc.expirationDate)
    )
      continue
    if (
      p.field === 'CATEGORY' &&
      p.categoryValue &&
      doc?.category &&
      p.categoryValue === doc.category
    )
      continue

    const existing = byField.get(p.field)
    if (!existing || (p.confidence ?? 0) > (existing.confidence ?? 0)) {
      byField.set(p.field, p)
    }
  }
  return [...byField.values()]
}

export function getExtractedFields(
  predictions: ExtractedFieldPrediction[] | null | undefined
): ExtractedField[] {
  if (!predictions) return []
  // Dedupe by field — if the same field is predicted more than once (e.g. both
  // the classify and extract passes emit DATE), keep the
  // highest-confidence one so the popover never shows duplicate rows.
  const byField = new Map<string, ExtractedField>()
  for (const p of predictions) {
    // Grouped EAV rows (MVR findings) are rolled up by getMvrCountFields —
    // listing each row here would flood the popover with one row per
    // accident/violation attribute.
    if (p.findingGroupId) continue
    const value = resolvePredictionValue(p)
    if (value == null) continue
    const field: ExtractedField = {
      field: p.field,
      label: EXTRACTED_FIELD_LABELS[p.field] ?? formatSnakeValue(p.field),
      value,
      confidence: p.confidence ?? 0,
      reviewStatus: resolveReviewStatus(p),
    }
    const existing = byField.get(p.field)
    if (!existing || field.confidence > existing.confidence) {
      byField.set(p.field, field)
    }
  }
  return [...byField.values()].sort((a, b) => {
    const ai = FIELD_ORDER.indexOf(a.field)
    const bi = FIELD_ORDER.indexOf(b.field)
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
  })
}

// MVR finding types that roll up to a count row in the "AI Extracted" popover,
// in display order. Maps MvrFindingType (schema.prisma) → row label.
const MVR_COUNT_ROWS = [
  { findingType: 'ACCIDENT', field: 'MVR_ACCIDENTS', label: 'Accidents' },
  {
    findingType: 'VIOLATION_MOVING',
    field: 'MVR_MOVING_VIOLATIONS',
    label: 'Moving violations',
  },
  {
    findingType: 'VIOLATION_NON_MOVING',
    field: 'MVR_NON_MOVING_VIOLATIONS',
    label: 'Non-moving violations',
  },
  { findingType: 'SUSPENSION', field: 'MVR_SUSPENSIONS', label: 'Suspensions' },
  {
    findingType: 'DISQUALIFICATION',
    field: 'MVR_DISQUALIFICATIONS',
    label: 'Disqualifications',
  },
  {
    findingType: 'LICENSE_ACTION',
    field: 'MVR_LICENSE_ACTIONS',
    label: 'License actions',
  },
]


// ── MVR finding types ─────────────────────────────────────────────────────────

export interface MvrFinding {
  findingGroupId: string
  findingType: string
  date: string | null
  city: string | null
  state: string | null
  description: string | null
  cmvInvolved: boolean | null
  injuries: boolean | null
  fatalities: boolean | null
}

// The full set of MVR finding types that count toward a noteworthy MVR signal
// — drives document selection in both the application-header AiFlagsPill and
// the card-level MvrFindingsPill so the two surfaces lock onto the same MVR
// document (even though they each filter findings to a narrower display
// scope). Without this shared selection set, the two pills could choose
// different MVRs when, e.g., the newest doc has only license actions and an
// older doc has accidents.
export const MVR_COUNTED_FINDING_TYPES = MVR_COUNT_ROWS.map((r) => r.findingType)

// PSP finding types that roll up to a count row in the "AI Extracted" popover,
// in display order. Maps PspFindingType (schema.prisma) → row label.
// INSPECTION is included because a clean-inspection history is worth
// surfacing. PSP_METADATA (MCMIS snapshot date) is not — see ENG-4376: it's
// report freshness metadata, not a driver event, and the row builder no
// longer emits it.
const PSP_COUNT_ROWS = [
  { findingType: 'CRASH', field: 'PSP_CRASHES', label: 'PSP crashes' },
  { findingType: 'INSPECTION', field: 'PSP_INSPECTIONS', label: 'PSP inspections' },
  { findingType: 'VIOLATION', field: 'PSP_VIOLATIONS', label: 'PSP violations' },
] as const

// The PSP finding types that drive PSP document selection — mirrors
// MVR_COUNTED_FINDING_TYPES. Applied to the same aiExtractedRows read-model,
// so PSP selection works identically to MVR selection on the applicant page.
export const PSP_COUNTED_FINDING_TYPES = PSP_COUNT_ROWS.map((r) => r.findingType)

// Alias — PSP findings share the same row shape as MVR findings once flattened
// from aiExtractedRows. Kept as a distinct type name so consumers reading
// PSP-specific code don't have to squint at an MVR-named type.
// The runtime helper pspFindingsFromExtractedRows lives below the
// mvrFindingsFromExtractedRows definition so const-hoisting isn't an issue.

// ── Read-model–assembled findings ───────────────────────────────────────────
// The Application-tab pills (MvrFindingsPill, AiFlagsPill) read the backend
// `aiExtractedRows` read-model rather than re-assembling findings on the client.
// Each finding-count row (Accidents, Moving violations, …) carries the
// per-finding `records`; this flattens them into the row shape the pills render.
// Source of truth for assembly is the backend (aiExtractedRows.ts) — this is a
// thin presentation adapter, not a re-implementation of buildMvrFindingsFromFindings.

// A label/value pair for a finding field beyond date/location/description
// (e.g. ACD code, Points, Case ID), assembled on the backend.
export interface ExtractedFindingDetail {
  label: string
  value: string
}

// The subset of an aiExtractedRows record this adapter reads. Loosely typed so
// it works against the generated GraphQL result without coupling to it.
export interface ExtractedFindingRecord {
  findingType?: string | null
  date?: string | null
  location?: string | null
  description?: string | null
  cmvInvolved?: boolean | null
  injuries?: boolean | null
  fatalities?: boolean | null
  details?: ExtractedFindingDetail[] | null
}

export interface ExtractedRowWithRecords {
  field: string
  records?: ExtractedFindingRecord[] | null
}

// One MVR finding, already display-formatted by the backend: `date` is ISO,
// `location` is the combined "city, state · county" heading, and the booleans
// are parsed. `key` is a stable React key (records carry no group id).
export interface MvrExtractedFinding {
  key: string
  findingType: string
  date: string | null
  location: string | null
  description: string | null
  cmvInvolved: boolean | null
  injuries: boolean | null
  fatalities: boolean | null
  // Every other field the AI extracted for this finding (ACD code, Points, …),
  // already labeled by the backend. Rendered as label/value lines in the pills.
  details: ExtractedFindingDetail[]
}

// Maps each incident-boolean's structured field to its detail-row label (as
// labeled by the backend in aiExtractedRows.constants.ts). Used to drop a
// boolean's duplicate detail line — but only when the structured field actually
// captured it (see below).
const INCIDENT_BOOLEAN_FIELDS = [
  { key: 'cmvInvolved', label: 'CMV involved' },
  { key: 'injuries', label: 'Injuries' },
  { key: 'fatalities', label: 'Fatalities' },
] as const

export const mvrFindingsFromExtractedRows = (
  rows: ExtractedRowWithRecords[] | null | undefined
): MvrExtractedFinding[] => {
  if (!rows) return []
  const findings: MvrExtractedFinding[] = []
  for (const row of rows) {
    ;(row.records ?? []).forEach((record, i) => {
      // Scalar/med-cert rows carry no records; only finding-count rows reach
      // here. A record without a findingType can't be categorized, so skip it.
      if (!record.findingType) return
      // The backend keeps the incident booleans in `details` too (for the
      // doc-preview hover, which reads records directly). The pills render them
      // as chips / Yes-No lines from the structured fields, so drop the
      // duplicate detail line — but ONLY when the structured field captured the
      // value. When parseMvrBoolean couldn't interpret the raw value the field
      // is null and no chip renders, so keep the raw detail line rather than
      // dropping the value from the pills entirely.
      const capturedAsFlag = new Set<string>(
        INCIDENT_BOOLEAN_FIELDS.filter((f) => record[f.key] != null).map(
          (f) => f.label
        )
      )
      findings.push({
        key: `${row.field}-${i}`,
        findingType: record.findingType,
        date: record.date ?? null,
        location: record.location ?? null,
        description: record.description ?? null,
        cmvInvolved: record.cmvInvolved ?? null,
        injuries: record.injuries ?? null,
        fatalities: record.fatalities ?? null,
        details: (record.details ?? []).filter(
          (d) => !capturedAsFlag.has(d.label)
        ),
      })
    })
  }
  // Records arrive grouped by finding type (Accidents block, then Violations,
  // …). Re-sort newest-first across all types so the pill lists interleave by
  // date — matching the global date-desc order buildMvrFindingsFromFindings used.
  return findings.sort((a, b) => {
    if (!a.date && !b.date) return 0
    if (!a.date) return 1
    if (!b.date) return -1
    return b.date.localeCompare(a.date)
  })
}

// Thin wrapper around mvrFindingsFromExtractedRows: PSP-emitted rows in
// aiExtractedRows carry PSP-specific findingTypes ('CRASH', 'INSPECTION',
// 'VIOLATION'), so the flatten logic is identical — we just re-export it
// under a PSP-named alias so callers reading psp-side code don't have to
// import the MVR-named helper. (cmvInvolved / injuries / fatalities are
// populated only for MVR ACCIDENT records; they stay null for PSP records,
// which is what the pills expect.)
export const pspFindingsFromExtractedRows = mvrFindingsFromExtractedRows

const parseMvrBoolean = (value: string | null | undefined): boolean | null => {
  if (value == null) return null
  const v = value.trim().toLowerCase()
  if (['true', 'yes', 'y', '1'].includes(v)) return true
  if (['false', 'no', 'n', '0'].includes(v)) return false
  const n = Number(v)
  if (!Number.isNaN(n)) return n > 0
  return null
}

// ── Server-assembled findings ─────────────────────────────────────────────────
// These functions operate on the structured Finding objects returned by the
// GraphQL findings resolver — column-type resolution is already done server-side.

interface ServerFindingField {
  field: string
  value: string | null
  confidence?: number
}

export interface ServerFinding {
  findingGroupId: string
  findingType: string | null
  fields: ServerFindingField[]
}

interface BuildFindingsConfig<T> {
  findingTypes: string[]
  mapper: (byField: Record<string, string | null>, finding: ServerFinding) => T
  compare?: (a: T, b: T) => number
}

export function buildFindings<T>(
  findings: ServerFinding[],
  { findingTypes, mapper, compare }: BuildFindingsConfig<T>
): T[] {
  const filtered = findings.filter(
    (f) => f.findingType && findingTypes.includes(f.findingType)
  )
  if (filtered.length === 0) return []
  const mapped = filtered.map((f) => {
    const byField = Object.fromEntries(f.fields.map((ff) => [ff.field, ff.value ?? null]))
    return mapper(byField, f)
  })
  return compare ? mapped.sort(compare) : mapped
}

export const buildMvrFindingsFromFindings = (findings: ServerFinding[]): MvrFinding[] =>
  buildFindings<MvrFinding>(findings, {
    findingTypes: MVR_COUNTED_FINDING_TYPES,
    mapper: (byField, f) => ({
      findingGroupId: f.findingGroupId,
      findingType: f.findingType!,
      date: byField['MVR_EVENT_DATE'] ?? null,
      city: byField['MVR_LOCATION_CITY'] ?? null,
      state: byField['MVR_LOCATION_STATE'] ?? null,
      description: byField['MVR_DESCRIPTION'] ?? null,
      cmvInvolved: parseMvrBoolean(byField['MVR_CMV_INVOLVED'] ?? null),
      injuries: parseMvrBoolean(byField['MVR_INJURIES'] ?? null),
      fatalities: parseMvrBoolean(byField['MVR_FATALITIES'] ?? null),
    }),
    compare: (a, b) => {
      if (!a.date && !b.date) return 0
      if (!a.date) return 1
      if (!b.date) return -1
      return b.date.localeCompare(a.date)
    },
  })

export function getMvrCountFieldsFromFindings(
  findings: ServerFinding[]
): ExtractedField[] {
  const fields: ExtractedField[] = []

  for (const row of MVR_COUNT_ROWS) {
    const matching = findings.filter((f) => f.findingType === row.findingType)
    if (matching.length === 0) continue
    const allConfidences = matching.flatMap((f) => f.fields.map((ff) => ff.confidence ?? 0))
    fields.push({
      field: row.field,
      label: row.label,
      value: String(matching.length),
      confidence: Math.min(...allConfidences),
      reviewStatus: null,
    })
  }

  const certFinding = findings.find((f) => f.findingType === 'MEDICAL_CERT')
  if (certFinding) {
    const fieldMap = Object.fromEntries(certFinding.fields.map((f) => [f.field, f]))
    const cat = fieldMap['MVR_MEDICAL_CERT_CATEGORY']
    if (cat?.value) fields.push({ field: 'MVR_MEDICAL_CERT_CATEGORY', label: 'Med cert category', value: cat.value, confidence: cat.confidence ?? 0, reviewStatus: null })
    const exp = fieldMap['MVR_MEDICAL_CERT_EXPIRATION']
    if (exp?.value) fields.push({ field: 'MVR_MEDICAL_CERT_EXPIRATION', label: 'Med cert expires', value: dayjs.utc(exp.value).format('MM/DD/YYYY'), confidence: exp.confidence ?? 0, reviewStatus: null })
  }

  return fields
}
